import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE_URL from '../../config';

interface Recipient {
    _id: string;
    name: string;
    phone: string;
    rsvpStatus: 'pending' | 'accepted' | 'declined' | 'maybe';
    deliveryStatus?: Record<string, { status: string; sentAt?: string; error?: string }>;
}

interface Campaign {
    _id: string;
    eventName: string;
    eventType: string;
    eventDate: string;
    eventVenue: string;
    eventCity: string;
    status: string;
    channels: string[];
    sentCount: number;
    failedCount: number;
    rsvpAccepted: number;
    rsvpDeclined: number;
    rsvpMaybe: number;
    totalRecipients: number;
}

export const InviteSummary: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAnonymousCampaign = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/anonymous/${id}`);
                if (!response.ok) {
                    throw new Error('Could not find anonymous campaign details or it is a private campaign.');
                }
                const data = await response.json();
                setCampaign(data.campaign);
                setRecipients(data.recipients || []);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchAnonymousCampaign();
        }
    }, [id]);

    const getEventEmoji = (type: string) => {
        switch (type) {
            case 'marriage': return '💒';
            case 'birthday': return '🎂';
            case 'housewarming': return '🏠';
            case 'anniversary': return '💍';
            case 'corporate': return '🏢';
            case 'engagement': return '💎';
            default: return '✉️';
        }
    };

    if (loading) {
        return <div className="invite-spinner-container"><div className="spinner"></div></div>;
    }

    if (error || !campaign) {
        return (
            <div className="wizard-step-card" style={{ textAlign: 'center', padding: '3rem' }}>
                <span style={{ fontSize: '3rem' }}>⚠️</span>
                <h3 style={{ marginTop: '1rem' }}>Invitation Not Found</h3>
                <p style={{ color: 'var(--invite-text-light)', marginTop: '0.5rem' }}>{error || 'The summary details could not be loaded.'}</p>
                <Link to="/invite/new" className="create-campaign-cta-btn" style={{ display: 'inline-block', marginTop: '1.5rem' }}>
                    Create New Invitation
                </Link>
            </div>
        );
    }

    const rsvpPending = campaign.totalRecipients - (campaign.rsvpAccepted + campaign.rsvpDeclined + campaign.rsvpMaybe);

    return (
        <div className="invite-summary-page" style={{ maxWidth: '850px', margin: '0 auto' }}>
            {/* Header info */}
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                <span style={{ fontSize: '3rem' }}>🚀</span>
                <h1 style={{ color: 'var(--invite-primary-dark)', fontSize: '2rem', marginTop: '1rem', fontWeight: 800 }}>
                    Invitation Dispatched Successfully!
                </h1>
                <p style={{ color: 'var(--invite-text-light)', marginTop: '0.35rem' }}>
                    Your invitations have been dispatched on the configured channels. Keep this link bookmarked to monitor live guest RSVPs!
                </p>
            </div>

            {/* Campaign info card */}
            <div className="wizard-step-card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '2rem' }}>{getEventEmoji(campaign.eventType)}</span>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>{campaign.eventName}</h2>
                            <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: 'var(--invite-text-light)' }}>
                                📅 {new Date(campaign.eventDate).toLocaleDateString()} · 📍 {campaign.eventVenue}, {campaign.eventCity}
                            </p>
                        </div>
                    </div>
                    <span className="status-badge completed" style={{ background: '#c6f6d5', color: '#22543d' }}>Dispatched</span>
                </div>
            </div>

            {/* Quota Banner */}
            <div className="quota-info-banner" style={{ marginBottom: '2rem' }}>
                <div className="quota-content">
                    <h4>✨ Save this Invitation to your Account?</h4>
                    <p>Register a free Shadigram account to save your templates, guest lists, send historical logs, and bypass the 10-recipient limit!</p>
                </div>
                <Link to="/invite/register" className="upgrade-plan-btn" style={{ textDecoration: 'none', background: 'var(--invite-primary)' }}>
                    Sign Up Free 🚀
                </Link>
            </div>

            {/* Stats grid */}
            <div className="stats-grid" style={{ marginBottom: '3rem' }}>
                <div className="stat-card">
                    <span className="stat-icon">👥</span>
                    <div className="stat-info">
                        <h3>{campaign.totalRecipients}</h3>
                        <p>Total Guests</p>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">✅</span>
                    <div className="stat-info">
                        <h3>{campaign.rsvpAccepted}</h3>
                        <p>Accepted</p>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">❌</span>
                    <div className="stat-info">
                        <h3>{campaign.rsvpDeclined}</h3>
                        <p>Declined</p>
                    </div>
                </div>
                <div className="stat-card">
                    <span className="stat-icon">❓</span>
                    <div className="stat-info">
                        <h3>{rsvpPending}</h3>
                        <p>Pending Reply</p>
                    </div>
                </div>
            </div>

            {/* Recipient status list */}
            <div className="wizard-step-card">
                <h3>Live RSVP Tracker</h3>
                <div className="preview-table-wrapper" style={{ marginTop: '1.5rem' }}>
                    <table className="recipients-table">
                        <thead>
                            <tr>
                                <th>Guest Name</th>
                                <th>Phone</th>
                                <th>RSVP Status</th>
                                <th>Delivery Channels</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recipients.map((rec, index) => (
                                <tr key={rec._id || index}>
                                    <td>{rec.name}</td>
                                    <td>{rec.phone}</td>
                                    <td>
                                        <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            background: rec.rsvpStatus === 'accepted' ? '#c6f6d5' : rec.rsvpStatus === 'declined' ? '#fed7d7' : rec.rsvpStatus === 'maybe' ? '#feebc8' : '#edf2f7',
                                            color: rec.rsvpStatus === 'accepted' ? '#22543d' : rec.rsvpStatus === 'declined' ? '#9b2c2c' : rec.rsvpStatus === 'maybe' ? '#dd6b20' : '#4a5568'
                                        }}>
                                            {rec.rsvpStatus}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                                            {campaign.channels.map(ch => {
                                                const stat = rec.deliveryStatus?.[ch];
                                                return (
                                                    <span key={ch} style={{
                                                        fontSize: '0.7rem',
                                                        padding: '1px 6px',
                                                        borderRadius: '3px',
                                                        background: stat?.status === 'sent' ? '#c6f6d5' : stat?.status === 'failed' ? '#fed7d7' : '#edf2f7',
                                                        color: stat?.status === 'sent' ? '#22543d' : stat?.status === 'failed' ? '#9b2c2c' : '#4a5568'
                                                    }}>
                                                        {ch.substring(0, 2).toUpperCase()}: {stat?.status || 'dispatched'}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default InviteSummary;
